/**
 * 🌐 API Service Configuration for Grented Frontend
 * 
 * 🔴 PRODUCTION DEPLOYMENT CHECKLIST:
 * ===================================
 * 1. Ensure VITE_API_BASE_URL environment variable is set to production API URL (WITHOUT /api suffix)
 * 2. Verify SSL/TLS certificates are properly configured for HTTPS
 * 3. Test API connectivity in production environment
 * 4. Review and adjust request timeout settings if needed
 * 5. Verify error handling for production scenarios
 * 6. Check CORS configuration on backend matches frontend domain
 * 
 * 📝 Required Environment Variables for Production:
 * - VITE_API_BASE_URL=https://grented.com (base URL only, no /api path)
 * 
 * 💡 API Path Convention:
 * - All API endpoints should include the full path starting with /api/
 * - Example: apiClient.get('/api/notifications/') NOT apiClient.get('/notifications/')
 */

import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { getToken, removeToken } from '../utils/token';

// 🔴 PRODUCTION: This will use VITE_API_BASE_URL from environment variables
// const API_URL = getApiBaseUrl();

// Funzione per mostrare modal di sessione scaduta con DOM vanilla
const showSessionExpiredModal = () => {
  // Evita di mostrare più modal contemporaneamente
  if (document.getElementById('session-expired-modal')) {
    return;
  }

  // Crea elementi DOM per il modal
  const overlay = document.createElement('div');
  overlay.id = 'session-expired-modal';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background-color: #1a1a1a;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 32px;
    max-width: 450px;
    width: 90%;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8);
    text-align: center;
    color: white;
  `;

  modal.innerHTML = `
    <div style="font-size: 48px; margin-bottom: 16px;">⏰</div>
    <h2 style="color: white; font-size: 24px; font-weight: bold; margin: 0 0 12px 0;">Sessione Scaduta</h2>
    <p style="color: rgba(255,255,255,0.8); font-size: 16px; line-height: 1.5; margin-bottom: 24px;">
      La tua sessione è scaduta per motivi di sicurezza. 
      Effettua nuovamente l'accesso per continuare.
    </p>
    <button id="session-modal-btn" style="
      background-color: #4f46e5;
      color: white;
      border: none;
      border-radius: 8px;
      padding: 12px 32px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 140px;
    ">Vai al Login</button>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Handler per il pulsante
  const button = document.getElementById('session-modal-btn');
  if (button) {
    button.addEventListener('click', () => {
      // Rimuovi il modal
      document.body.removeChild(overlay);
      
      // Salva la pagina corrente per il redirect
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        localStorage.setItem('redirect_after_login', currentPath);
      }
      
      // Reindirizza al login
      window.location.href = '/login';
    });

    // Effetto hover
    button.addEventListener('mouseenter', () => {
      button.style.opacity = '0.9';
    });
    button.addEventListener('mouseleave', () => {
      button.style.opacity = '1';
    });
  }
};

// Crea istanza axios con configurazione base
const apiClient = axios.create({
  baseURL: '/api/', // Usa path relativo con trailing slash per sfruttare il proxy di Vite in sviluppo
  timeout: 10000,
  // Non impostare globalmente 'Content-Type': Axios lo gestisce automaticamente.
  // Per FormData imposta multipart con boundary; per JSON usa application/json.
  // withCredentials: true, // Non necessario con proxy Vite (stesso dominio)
});

// Interceptor per aggiungere automaticamente il token alle richieste
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Non aggiungere token alle richieste di login
    if (config.url?.includes('/users/login')) {
      return config;
    }
    
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag per evitare popup duplicati
let sessionExpiredShown = false;

// Interceptor per gestire risposte e errori
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Se ricevi 401, significa che il token è scaduto o non valido
    if (error.response?.status === 401) {
      // Non mostrare il modal per errori di login (credenziali errate)
  const isLoginRequest = error.config?.url?.includes('/users/login');
      
      if (!isLoginRequest && !sessionExpiredShown) {
        sessionExpiredShown = true; // Evita popup duplicati
        
        // Rimuovi il token scaduto
        removeToken();
        
        // Lista delle pagine pubbliche che non richiedono autenticazione
        const publicPaths = ['/login', '/register', '/'];
        const currentPath = window.location.pathname;
        
        // Mostra il modal se non siamo in una pagina pubblica
        if (!publicPaths.includes(currentPath)) {
          // Mostra un alert semplice invece del modal (temporaneo)
          alert('Sessione scaduta. Verrai reindirizzato al login.');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// Export della funzione per impostare handler personalizzato
export const setSessionExpiredHandler = (handler: () => void) => {
  // Al momento usiamo il modal DOM vanilla, ma questa funzione 
  // permette di sovrascrivere il comportamento se necessario
  return handler;
};
